import { useEffect, useState } from 'react';
import type { FeedbackStatusResponse } from '@awwyeah/shared';
import { useUser } from '../auth';
import { fetchFeedbackStatus } from './api';
import { readCachedStatus, writeCachedStatus } from './storage';

/**
 * Whether the signed-in member may send ideas / is the recipient. Starts from the
 * last-known answer on this device (so the link shows offline), then refreshes. `null`
 * while signed out or before anything is known — callers render nothing then.
 */
export function useFeedbackStatus(): FeedbackStatusResponse | null {
  const { user } = useUser();
  const userId = user?.id ?? null;
  const [status, setStatus] = useState<FeedbackStatusResponse | null>(() =>
    userId ? readCachedStatus(userId) : null,
  );

  useEffect(() => {
    if (!userId) {
      setStatus(null);
      return undefined;
    }
    setStatus(readCachedStatus(userId));
    let cancelled = false;
    fetchFeedbackStatus()
      .then((fresh) => {
        if (cancelled) return;
        writeCachedStatus(userId, fresh);
        setStatus(fresh);
      })
      .catch(() => {
        // Offline or a hiccup: keep whatever we last knew.
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return status;
}
